import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, authImageUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users, Building2, Layers, Search, MapPin, BookOpen, Loader2, Crown } from "lucide-react";
import { Laurel } from "@/components/LaurelBadge";

const StatCard = ({ icon: Icon, value, label, testid, note }) => (
  <div className="relative bg-white rounded-lg border border-navy/10 p-6 overflow-hidden" data-testid={testid}>
    <div className="absolute top-0 left-0 right-0 h-1 bg-gold" />
    <div className="flex items-start justify-between">
      <div>
        <p className="font-display text-4xl sm:text-5xl font-bold text-navy leading-none">{value}</p>
        <p className="mt-3 text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">{label}</p>
        {note && (
          <p className="mt-1.5 text-sm font-semibold text-gold flex items-center gap-1.5" data-testid={`${testid}-note`}>
            <Laurel className="w-4 h-4 shrink-0" />{note}
          </p>
        )}
      </div>
      <div className="w-11 h-11 rounded-md bg-navy/5 flex items-center justify-center">
        <Icon className="w-5 h-5 text-navy" />
      </div>
    </div>
  </div>
);

const Initials = ({ nome, cognome }) => (
  <div className="w-14 h-14 rounded-md bg-navy flex items-center justify-center border border-gold/40 shrink-0">
    <span className="font-display text-gold font-bold text-lg">
      {(nome?.[0] || "").toUpperCase()}{(cognome?.[0] || "").toUpperCase()}
    </span>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState({ total_members: 0, total_clubs: 0, total_divisions: 0 });
  const [filters, setFilters] = useState({ clubs: [], divisions: [] });
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [club, setClub] = useState(searchParams.get("club") || "all");
  const [divisione, setDivisione] = useState("all");
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(60);
  const [downloading, setDownloading] = useState(false);
  const [clubInfo, setClubInfo] = useState(null);
  const [divInfo, setDivInfo] = useState(null);

  useEffect(() => {
    if (club !== "all") {
      api.get(`/org/clubs/${encodeURIComponent(club)}`).then((r) => setClubInfo(r.data)).catch(() => setClubInfo(null));
    } else {
      setClubInfo(null);
    }
  }, [club]);

  useEffect(() => {
    if (divisione !== "all") {
      api.get(`/org/divisions/${encodeURIComponent(divisione)}/summary`).then((r) => setDivInfo(r.data)).catch(() => setDivInfo(null));
    } else {
      setDivInfo(null);
    }
  }, [divisione]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/directory/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Directory-Kiwanis-Distretto-Italia-San-Marino.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Impossibile generare il PDF. Riprova.");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const c = searchParams.get("club");
    if (c) setClub(c);
  }, [searchParams]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/filters").then((r) => setFilters(r.data));
  }, []);

  const hasFilter = q.trim() !== "" || club !== "all" || divisione !== "all";

  const fetchMembers = useCallback(async () => {
    if (!(q.trim() !== "" || club !== "all" || divisione !== "all")) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (club !== "all") params.club = club;
    if (divisione !== "all") params.divisione = divisione;
    const { data } = await api.get("/members", { params });
    setMembers(data);
    setVisible(60);
    setLoading(false);
  }, [q, club, divisione]);

  useEffect(() => {
    const t = setTimeout(fetchMembers, 250);
    return () => clearTimeout(t);
  }, [fetchMembers]);

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-gold">Panoramica del Distretto</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-navy mt-2 tracking-tight">Elenco Soci</h1>
        </div>
        <button onClick={downloadPdf} disabled={downloading} data-testid="download-directory-pdf-btn"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-hover transition-colors disabled:opacity-60 shrink-0 ${user?.role === "admin" ? "" : "hidden"}`}>
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
          {downloading ? "Generazione PDF…" : "Scarica Directory (PDF)"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard icon={Users} value={stats.total_members} label="Soci totali" testid="stat-members" />
        <StatCard icon={Building2} value={stats.total_clubs} label="Club" testid="stat-clubs" />
        <StatCard icon={Layers} value={stats.total_divisions} label="Divisioni" testid="stat-divisions" />
      </div>

      <div className="bg-white rounded-lg border border-navy/10 p-4 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative md:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o cognome…"
            className="pl-10 h-11" data-testid="member-search-input" />
        </div>
        <Select value={club} onValueChange={setClub}>
          <SelectTrigger className="h-11" data-testid="filter-club"><SelectValue placeholder="Tutti i club" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i club</SelectItem>
            {filters.clubs.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={divisione} onValueChange={setDivisione}>
          <SelectTrigger className="h-11" data-testid="filter-divisione"><SelectValue placeholder="Tutte le divisioni" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le divisioni</SelectItem>
            {[...filters.divisions]
              .sort((a, b) => {
                const n = (s) => { const m = String(s).match(/\d+/); return m ? parseInt(m[0], 10) : 9999; };
                return n(a) - n(b) || String(a).localeCompare(b);
              })
              .map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        {!hasFilter ? (
          <div className="text-center py-16 border border-dashed border-navy/15 rounded-lg bg-white/50" data-testid="search-prompt">
            <Search className="w-8 h-8 text-navy/25 mx-auto mb-3" />
            <p className="text-navy-deep font-semibold">Cerca un socio per iniziare</p>
            <p className="text-sm text-muted-foreground mt-1">Digita un nome o cognome, oppure filtra per club o divisione.</p>
          </div>
        ) : (
          <>
            {club !== "all" && clubInfo && (
              <div className="bg-navy text-white rounded-lg overflow-hidden mb-6" data-testid="dashboard-club-header">
                <div className="px-6 py-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-gold flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5" /> Club
                    </p>
                    <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1 leading-tight">{clubInfo.club}</h2>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-white/70 mt-2">
                      {clubInfo.division && <span className="flex items-center gap-1.5"><Layers className="w-4 h-4 text-gold" />{clubInfo.division}</span>}
                      <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-gold" />{clubInfo.members_count} soci</span>
                    </div>
                  </div>
                  {clubInfo.president && (
                    <Link to={`/soci/${clubInfo.president.id}`} data-testid="dashboard-club-president"
                      className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-md px-4 py-3 transition-colors shrink-0">
                      {clubInfo.president.photo_url ? (
                        <img src={authImageUrl(clubInfo.president.photo_url)} alt={clubInfo.president.cognome}
                          className="w-12 h-12 rounded-md object-cover border border-gold/40"
                          onError={(e) => { e.target.style.display = "none"; }} />
                      ) : (
                        <div className="w-12 h-12 rounded-md bg-navy-deep flex items-center justify-center border border-gold/40">
                          <span className="font-display text-gold font-bold">
                            {(clubInfo.president.nome?.[0] || "").toUpperCase()}{(clubInfo.president.cognome?.[0] || "").toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="text-left leading-tight">
                        <p className="text-[10px] uppercase tracking-wider text-gold font-bold flex items-center gap-1"><Crown className="w-3 h-3" />Presidente</p>
                        <p className="text-sm font-semibold">{clubInfo.president.nome} {clubInfo.president.cognome}</p>
                      </div>
                    </Link>
                  )}
                </div>
              </div>
            )}
            {divisione !== "all" && divInfo && (
              <div className="bg-navy text-white rounded-lg overflow-hidden mb-6" data-testid="dashboard-division-header">
                <div className="px-6 py-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-gold flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5" /> Divisione
                    </p>
                    <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1 leading-tight">{divInfo.division}</h2>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-white/70 mt-2">
                      <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4 text-gold" />{divInfo.clubs_count} club</span>
                      <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-gold" />{divInfo.members_count} soci</span>
                    </div>
                  </div>
                  {divInfo.lieutenant && (
                    <Link to={`/soci/${divInfo.lieutenant.id}`} data-testid="dashboard-division-lieutenant"
                      className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-md px-4 py-3 transition-colors shrink-0">
                      {divInfo.lieutenant.photo_url ? (
                        <img src={authImageUrl(divInfo.lieutenant.photo_url)} alt={divInfo.lieutenant.cognome}
                          className="w-12 h-12 rounded-md object-cover border border-gold/40"
                          onError={(e) => { e.target.style.display = "none"; }} />
                      ) : (
                        <div className="w-12 h-12 rounded-md bg-navy-deep flex items-center justify-center border border-gold/40">
                          <span className="font-display text-gold font-bold">
                            {(divInfo.lieutenant.nome?.[0] || "").toUpperCase()}{(divInfo.lieutenant.cognome?.[0] || "").toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="text-left leading-tight">
                        <p className="text-[10px] uppercase tracking-wider text-gold font-bold flex items-center gap-1"><Crown className="w-3 h-3" />Luogotenente</p>
                        <p className="text-sm font-semibold">{divInfo.lieutenant.nome} {divInfo.lieutenant.cognome}</p>
                      </div>
                    </Link>
                  )}
                </div>
              </div>
            )}
            <p className="text-sm text-muted-foreground mb-4" data-testid="results-count">
              {loading ? "Ricerca…" : `${members.length} soci trovati`}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {members.slice(0, visible).map((m) => {
                const clubRole = club !== "all" && m.memberships
                  ? (m.memberships.find((x) => x.club === club)?.ruolo || "")
                  : "";
                const districtLabel = (m.district_roles && m.district_roles[0])
                  || (m.lieutenant_of && m.lieutenant_of.length ? "Luogotenente Governatore" : "");
                return (
                <Link key={m.id} to={`/soci/${m.id}`} data-testid={`member-card-${m.id}`}
                  className="group bg-white rounded-lg border border-navy/10 p-5 flex gap-4 items-center hover:shadow-md hover:-translate-y-1 transition-transform transition-shadow duration-200">
                  {m.photo_url ? (
                    <img src={authImageUrl(m.photo_url)} alt={m.cognome}
                      className="w-14 h-14 rounded-md object-cover border border-gold/40 shrink-0"
                      onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <Initials nome={m.nome} cognome={m.cognome} />
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-navy-deep truncate group-hover:text-navy flex items-center gap-1.5">
                      <span className="truncate">{m.nome} {m.cognome}</span>
                      {m.onorario && <span title="Socio Onorario" className="shrink-0"><Laurel className="w-4 h-4 text-gold" /></span>}
                    </p>
                    {clubRole && <p className="text-xs uppercase tracking-wider text-gold font-bold truncate">{clubRole}</p>}
                    {districtLabel && <p className="text-xs uppercase tracking-wider text-navy font-bold truncate">{districtLabel}</p>}
                    <p className="text-sm text-muted-foreground flex items-start gap-1 mt-1">
                      <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span className="line-clamp-2">{(m.clubs && m.clubs.length ? m.clubs : [m.club]).filter(Boolean).join(" · ") || "—"}</span>
                    </p>
                  </div>
                </Link>
              );})}
            </div>
            {!loading && members.length === 0 && (
              <div className="text-center py-16 text-muted-foreground" data-testid="no-results">Nessun socio corrisponde ai criteri di ricerca.</div>
            )}
            {!loading && visible < members.length && (
              <div className="text-center mt-8">
                <button onClick={() => setVisible((v) => v + 60)} data-testid="load-more-btn"
                  className="px-6 py-2.5 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-hover transition-colors">
                  Carica altri ({members.length - visible} rimanenti)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
